import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Laptop2, Smartphone, CreditCard, Server, KeySquare, Monitor, Package,
  Plus, UserCheck, RotateCcw, Search, IdCard
} from "lucide-react";

const TYPES = ["Laptop","Desktop","Mobile","SIM","Access Card","ID Card","Microsoft 365 License","VPN Access","Server Access","Headphones","Monitor"];

const ICONS = {
  Laptop: Laptop2, Desktop: Monitor, Mobile: Smartphone, SIM: CreditCard,
  "Access Card": KeySquare, "ID Card": IdCard, "Microsoft 365 License": Package,
  "VPN Access": Server, "Server Access": Server, Monitor, Headphones: Package,
};

const STATUS_STYLE = {
  Available: "bg-emerald-50 text-emerald-700",
  Assigned: "bg-red-50 text-red-700",
  Retired: "bg-slate-100 text-slate-600",
  Lost: "bg-orange-50 text-orange-700",
};

function money(n) { return "₹ " + Number(n || 0).toLocaleString("en-IN"); }

export default function Assets() {
  const { user } = useAuth();
  const isReviewer = user?.role === "admin" || user?.role === "hr";
  const [assets, setAssets] = useState([]);
  const [mine, setMine] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ tag:"", type:"Laptop", make:"", model:"", serial:"", purchase_date:"", cost:0, notes:"" });
  const [assignFor, setAssignFor] = useState(null);
  const [returnFor, setReturnFor] = useState(null);
  const [assignEmp, setAssignEmp] = useState("");
  const [returnForm, setReturnForm] = useState({ hardware_ok:true, charger_returned:true, accessories_returned:true, data_wiped:true, notes:"" });

  const load = async () => {
    if (isReviewer) {
      const [a, e] = await Promise.all([api.get("/assets"), api.get("/employees")]);
      setAssets(a.data); setEmployees(e.data);
    }
    if (user?.employee_id) {
      const m = await api.get("/assets/mine");
      setMine(m.data);
    }
  };
  useEffect(() => { load(); }, [user?.employee_id, isReviewer]);

  const source = isReviewer ? assets : mine;
  const filtered = source.filter(a =>
    (typeFilter === "all" || a.type === typeFilter) &&
    (statusFilter === "all" || a.status === statusFilter) &&
    (!q || a.tag.toLowerCase().includes(q.toLowerCase())
        || (a.model||"").toLowerCase().includes(q.toLowerCase())
        || (a.assigned_to_name||"").toLowerCase().includes(q.toLowerCase()))
  );

  const stats = useMemo(() => {
    const byType = {};
    source.forEach(a => { byType[a.type] = (byType[a.type] || 0) + 1; });
    return {
      total: source.length,
      assigned: source.filter(a => a.status === "Assigned").length,
      available: source.filter(a => a.status === "Available").length,
      value: source.reduce((s,a)=>s + (a.cost||0), 0),
      byType,
    };
  }, [source]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/assets", { ...form, cost: Number(form.cost || 0) });
      toast.success(`${form.tag} added to inventory`);
      setShowNew(false);
      setForm({ tag:"", type:"Laptop", make:"", model:"", serial:"", purchase_date:"", cost:0, notes:"" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const doAssign = async () => {
    if (!assignEmp) { toast.error("Pick an employee"); return; }
    try {
      await api.put(`/assets/${assignFor.id}/assign`, { employee_id: assignEmp });
      toast.success(`${assignFor.tag} assigned`);
      setAssignFor(null); setAssignEmp("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const doReturn = async () => {
    try {
      await api.put(`/assets/${returnFor.id}/return`, returnForm);
      toast.success(`${returnFor.tag} returned`);
      setReturnFor(null);
      setReturnForm({ hardware_ok:true, charger_returned:true, accessories_returned:true, data_wiped:true, notes:"" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="assets-root">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase font-semibold tracking-[0.1em] text-slate-500">Asset Inventory</div>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-slate-900">Every laptop, every SIM.</h1>
          <p className="mt-1 text-sm text-slate-500">Assign, track and reclaim company assets with a proper return checklist.</p>
        </div>
        {isReviewer && (
          <Button onClick={() => setShowNew(v => !v)} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="add-asset-btn">
            <Plus className="h-4 w-4 mr-1.5" strokeWidth={1.8} /> {showNew ? "Close" : "Add asset"}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total assets" value={stats.total} icon={Package} tone="red" />
        <StatCard label="Assigned" value={stats.assigned} icon={UserCheck} tone="green" />
        <StatCard label="Available" value={stats.available} icon={Laptop2} tone="orange" />
        <StatCard label="Inventory value" value={money(stats.value)} icon={CreditCard} tone="slate" />
      </div>

      {showNew && (
        <Card className="p-6 rounded-xl border-slate-200/60" data-testid="asset-form-card">
          <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Field label="Asset tag *"><Input required value={form.tag} onChange={e => setForm({...form, tag: e.target.value})} data-testid="asset-tag" placeholder="TT-LAP-025" /></Field>
            <Field label="Type *">
              <Select value={form.type} onValueChange={v => setForm({...form, type: v})}>
                <SelectTrigger data-testid="asset-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Make"><Input value={form.make} onChange={e => setForm({...form, make: e.target.value})} placeholder="Dell / Apple / Cisco" /></Field>
            <Field label="Model"><Input value={form.model} onChange={e => setForm({...form, model: e.target.value})} /></Field>
            <Field label="Serial"><Input value={form.serial} onChange={e => setForm({...form, serial: e.target.value})} /></Field>
            <Field label="Purchase date"><Input type="date" value={form.purchase_date} onChange={e => setForm({...form, purchase_date: e.target.value})} /></Field>
            <Field label="Cost (₹)"><Input type="number" value={form.cost} onChange={e => setForm({...form, cost: e.target.value})} /></Field>
            <Field label="Notes"><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></Field>
            <div className="md:col-span-4 flex justify-end">
              <Button type="submit" className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="asset-submit">Add asset</Button>
            </div>
          </form>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={1.5} />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by tag, model or holder" className="pl-9 h-10" data-testid="asset-search" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-44 h-10" data-testid="asset-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 h-10" data-testid="asset-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="Available">Available</SelectItem>
            <SelectItem value="Assigned">Assigned</SelectItem>
            <SelectItem value="Retired">Retired</SelectItem>
            <SelectItem value="Lost">Lost</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="rounded-xl border-slate-200/60 overflow-hidden">
        <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-slate-100 text-[10px] uppercase font-semibold tracking-wider text-slate-500">
          <div className="col-span-4">Asset</div>
          <div className="col-span-2">Serial</div>
          <div className="col-span-3">Holder</div>
          <div className="col-span-1">Cost</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {filtered.map(a => {
          const Icon = ICONS[a.type] || Package;
          return (
            <div key={a.id} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-slate-50 items-center hover:bg-slate-50/50 transition-all" data-testid={`asset-row-${a.tag}`}>
              <div className="col-span-4 flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-red-50 text-[#DC2626] grid place-items-center shrink-0">
                  <Icon className="h-4 w-4" strokeWidth={1.5} />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 font-mono">{a.tag}</div>
                  <div className="text-xs text-slate-500 truncate">{a.type} · {a.make || "—"} {a.model || ""}</div>
                </div>
              </div>
              <div className="col-span-2 text-xs text-slate-600 font-mono truncate">{a.serial || "—"}</div>
              <div className="col-span-3">
                <div className="text-sm text-slate-900 truncate">{a.assigned_to_name || <span className="text-slate-400">—</span>}</div>
                <Badge className={`text-[10px] mt-0.5 border-0 ${STATUS_STYLE[a.status]}`}>{a.status}</Badge>
              </div>
              <div className="col-span-1 text-xs text-slate-600">{money(a.cost)}</div>
              <div className="col-span-2 flex justify-end gap-2">
                {isReviewer && a.status === "Available" && (
                  <Button size="sm" variant="outline" className="h-8 border-red-200 text-[#DC2626]" onClick={() => setAssignFor(a)} data-testid={`assign-${a.tag}`}>
                    <UserCheck className="h-3.5 w-3.5 mr-1" strokeWidth={1.8} /> Assign
                  </Button>
                )}
                {isReviewer && a.status === "Assigned" && (
                  <Button size="sm" variant="outline" className="h-8 border-slate-300" onClick={() => setReturnFor(a)} data-testid={`return-${a.tag}`}>
                    <RotateCcw className="h-3.5 w-3.5 mr-1" strokeWidth={1.8} /> Return
                  </Button>
                )}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && <div className="p-10 text-center text-sm text-slate-500">No assets match your filters.</div>}
      </Card>

      {/* Assign dialog */}
      <Dialog open={!!assignFor} onOpenChange={(o) => !o && setAssignFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign {assignFor?.tag}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Employee</Label>
            <Select value={assignEmp} onValueChange={setAssignEmp}>
              <SelectTrigger data-testid="assign-employee-select"><SelectValue placeholder="Pick an employee" /></SelectTrigger>
              <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name} · {e.employee_code}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex justify-end pt-2">
              <Button onClick={doAssign} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="confirm-assign">Confirm</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Return dialog */}
      <Dialog open={!!returnFor} onOpenChange={(o) => !o && setReturnFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Return {returnFor?.tag} from {returnFor?.assigned_to_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs uppercase font-semibold tracking-wider text-slate-500">Exit checklist</div>
            {[
              ["hardware_ok", "Hardware in good condition"],
              ["charger_returned", "Charger / power adapter returned"],
              ["accessories_returned", "Accessories (mouse, bag, etc.) returned"],
              ["data_wiped", "Data wiped / reset to factory"],
            ].map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 text-sm">
                <Checkbox checked={returnForm[k]} onCheckedChange={(v) => setReturnForm({...returnForm, [k]: !!v})} data-testid={`check-${k}`} />
                {label}
              </label>
            ))}
            <div>
              <Label>Notes</Label>
              <Textarea rows={2} value={returnForm.notes} onChange={e => setReturnForm({...returnForm, notes: e.target.value})} data-testid="return-notes" />
            </div>
            <div className="flex justify-end pt-2">
              <Button onClick={doReturn} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="confirm-return">Mark returned</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone }) {
  const tones = { red: "text-[#DC2626] bg-red-50", green: "text-emerald-600 bg-emerald-50", orange: "text-orange-600 bg-orange-50", slate: "text-slate-700 bg-slate-100" };
  return (
    <Card className="p-5 rounded-xl border-slate-200/60">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">{label}</div>
          <div className="mt-1 font-display text-2xl font-bold text-slate-900">{value}</div>
        </div>
        <div className={`h-10 w-10 rounded-lg grid place-items-center ${tones[tone]}`}><Icon className="h-5 w-5" strokeWidth={1.5} /></div>
      </div>
    </Card>
  );
}
